import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { notificationsAPI } from '../../services/api';
import { usePostHog } from '@posthog/react';

const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const posthog = usePostHog();

  const [unreadCount, setUnreadCount] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);

  // Listen for alerts-read events from AlertsPage to refresh the badge
  useEffect(() => {
    const handler = () => setRefreshTick(t => t + 1);
    window.addEventListener('alerts-read', handler);
    return () => window.removeEventListener('alerts-read', handler);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) { setUnreadCount(0); return; }
    let cancelled = false;
    notificationsAPI.unreadCount().then(data => {
      if (!cancelled) {
        const count = typeof data === 'number' ? data : (data.unread_count ?? data.count ?? 0);
        setUnreadCount(count);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isAuthenticated, location.pathname, refreshTick]);

  const isAdvisor = () => user?.is_advisor === true;

  const handleLogout = async () => {
    posthog.capture('user_logged_out');
    posthog.reset();
    await logout();
    navigate('/');
    setMenuOpen(false);
  };

  const closeMenu = () => setMenuOpen(false);

  const navLinkClass = (path) =>
    `flex items-center space-x-3 px-4 min-h-[44px] font-medium transition-colors hover:text-[#615DEC] ${
      location.pathname === path || location.pathname.startsWith(path + '/')
        ? 'text-[#615DEC]'
        : 'text-gray-700'
    }`;

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 relative z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center" onClick={closeMenu}>
              <img
                src="/oko-logo.png"
                alt="OKO Funding Assistant"
                className="h-8 w-auto"
              />
            </Link>
          </div>

          {/* Desktop nav */}
          <nav className="hidden lg:flex space-x-8">
            <Link
              to="/funding"
              className={`flex items-center space-x-1 font-medium transition-colors hover:text-[#615DEC] ${location.pathname === '/funding' ? 'text-[#615DEC] underline' : 'text-gray-600'}`}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span>Find</span>
            </Link>
            {isAuthenticated && isAdvisor() && (
              <Link
                to="/farm-management"
                className={`flex items-center space-x-1 font-medium transition-colors hover:text-[#615DEC] ${location.pathname.startsWith('/farm-management') ? 'text-[#615DEC] underline' : 'text-gray-600'}`}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8v-4a1 1 0 011-1h2a1 1 0 011 1v4m-3 0h3" />
                </svg>
                <span>Farms</span>
              </Link>
            )}
            {isAuthenticated && (
              <Link
                to="/funding-lists"
                className={`flex items-center space-x-1 font-medium transition-colors hover:text-[#615DEC] ${location.pathname.startsWith('/funding-lists') ? 'text-[#615DEC] underline' : 'text-gray-600'}`}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <span>My Lists</span>
              </Link>
            )}
            {isAuthenticated && (
              <Link
                to="/alerts"
                className={`relative flex items-center space-x-1 font-medium transition-colors hover:text-[#615DEC] ${location.pathname.startsWith('/alerts') ? 'text-[#615DEC] underline' : 'text-gray-600'}`}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <span>Alerts</span>
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-3.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[11px] font-bold rounded-full bg-red-500 text-white">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
            )}
          </nav>

          {/* Desktop auth actions */}
          <div className="hidden lg:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 text-gray-600 transition-colors hover:text-[#615DEC]"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{user?.first_name || user?.username}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 text-gray-600 hover:text-red-600"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-gray-600 font-medium transition-colors hover:text-[#615DEC]"
                >
                  Login
                </Link>
                <Link to="/signup" className="btn-primary">
                  Free Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Mobile/tablet: login shortcut + hamburger */}
          <div className="flex lg:hidden items-center space-x-1">
            {!isAuthenticated && (
              <Link
                to="/login"
                className="text-sm font-medium text-gray-600 hover:text-[#615DEC] px-3 min-h-[44px] flex items-center"
              >
                Login
              </Link>
            )}
            <button
              onClick={() => setMenuOpen(true)}
              className="inline-flex items-center justify-center w-11 h-11 rounded-md text-gray-600 hover:text-[#615DEC] hover:bg-gray-100 transition-colors"
              aria-label="Open menu"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Off-canvas drawer — mobile/tablet only */}
      {menuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/40 lg:hidden"
            onClick={closeMenu}
          />
          {/* Drawer */}
          <div className="fixed top-0 right-0 bottom-0 z-50 w-72 bg-white shadow-xl flex flex-col lg:hidden">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <img src="/oko-logo.png" alt="OKO" className="h-7 w-auto" />
              <button
                onClick={closeMenu}
                className="inline-flex items-center justify-center w-11 h-11 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                aria-label="Close menu"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Nav links */}
            <nav className="flex-1 overflow-y-auto py-2">
              <Link to="/funding" onClick={closeMenu} className={navLinkClass('/funding')}>
                <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Find Opportunities</span>
              </Link>

              {isAuthenticated && (
                <Link to="/funding-lists" onClick={closeMenu} className={navLinkClass('/funding-lists')}>
                  <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  <span>My Lists</span>
                </Link>
              )}

              {isAuthenticated && (
                <Link to="/alerts" onClick={closeMenu} className={navLinkClass('/alerts')}>
                  <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <span>Alerts</span>
                  {unreadCount > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full bg-red-500 text-white">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
              )}

              {isAuthenticated && isAdvisor() && (
                <Link to="/farm-management" onClick={closeMenu} className={navLinkClass('/farm-management')}>
                  <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8v-4a1 1 0 011-1h2a1 1 0 011 1v4m-3 0h3" />
                  </svg>
                  <span>Farm Management</span>
                </Link>
              )}

              {isAuthenticated && (
                <Link to="/profile" onClick={closeMenu} className={navLinkClass('/profile')}>
                  <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{user?.first_name || user?.username || 'Profile'}</span>
                </Link>
              )}
            </nav>

            {/* Auth footer */}
            <div className="border-t border-gray-200 py-2 flex-shrink-0">
              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-4 min-h-[44px] text-red-600 hover:bg-red-50 transition-colors font-medium"
                >
                  <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Logout</span>
                </button>
              ) : (
                <div className="px-4 py-2">
                  <Link to="/signup" onClick={closeMenu} className="btn-primary block text-center">
                    Free Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
